import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DatePicker, Input, Select } from 'antd';
import dayjs from 'dayjs';
import BaseForm from '../../../../../components/form/BaseForm';
import { ListSearchBox } from '../../../../../components/list';
import {
  PromotionScheduleListFilter,
  PromotionScheduleStatus,
  PromotionScheduleTrack,
  PromotionScheduleType,
} from '../../../../../lib/api/promotionSchedule';
import { promotionScheduleQuery } from '../../queries';
import { MANAGER_UNASSIGNED_VALUE, STATUS_LABEL, TRACK_META, TRACK_ORDER, TYPE_LABEL } from '../../constants';
import { DATE_FORMAT } from '../../utils';
import { StyledFieldRow, StyledHint, StyledLabeled } from './styles';

type Props = {
  filter: PromotionScheduleListFilter;
  onChange: (filter: PromotionScheduleListFilter) => void;
  onSearch: () => void;
  onClear: () => void;
  /** 적용된 조건 수 — 접었을 때 헤더에 표시한다 */
  activeCount?: number;
};

const TRACK_OPTIONS = TRACK_ORDER.map((track) => ({ value: track, label: TRACK_META[track].label }));

const STATUS_OPTIONS = (Object.keys(STATUS_LABEL) as PromotionScheduleStatus[]).map((status) => ({
  value: status,
  label: STATUS_LABEL[status],
}));

const TYPE_OPTIONS = (Object.keys(TYPE_LABEL) as PromotionScheduleType[]).map((type) => ({
  value: type,
  label: TYPE_LABEL[type],
}));

/**
 * 연결 여부 선택지.
 *
 * ⚠️ antd `Select` 의 "선택 안 함"과 `false` 를 구분해야 한다 — `false` 는
 * *"연결이 없는 것만"* 이라는 **의미 있는 조건**이고, 미선택은 *"상관없음"* 이다.
 * `allowClear` 로 미선택(undefined)으로 돌아갈 수 있게 둔다.
 */
const LINKED_OPTIONS = [
  { value: true, label: '연결됨' },
  { value: false, label: '미연결' },
];

const FILTER_WIDTH = 260;

/**
 * 프로모션 리스트 검색 조건 (AMZUWEB-682).
 *
 * 🔴 **연결 여부 필터가 이 화면의 핵심이다.** dev 실측 421건 중 기획전 연결이 1건(0.2%)이라
 * *"확정인데 아직 기획전이 안 붙은 건"* 을 뽑는 것이 MD 에게 가장 쓸모 있는 질의인데,
 * 타임라인에서는 훑을 방법이 없다.
 *
 * 값 변경은 화면 상태만 바꾸고 조회는 **검색 버튼**에서 일어난다 —
 * 어드민 전역의 `SearchBox` 관례(42곳)를 따른다.
 */
export const ScheduleListFilter = ({
  filter,
  onChange,
  onSearch,
  onClear,
  activeCount = 0,
}: Props): React.JSX.Element => {
  const { data: managers = [] } = useQuery(promotionScheduleQuery.managers);

  const patch = (partial: Partial<PromotionScheduleListFilter>): void => {
    onChange({ ...filter, ...partial });
  };

  const managerOptions = [
    { value: MANAGER_UNASSIGNED_VALUE, label: '담당자 미지정' },
    ...managers.map((manager) => ({
      value: manager.id,
      label: manager.department ? `${manager.name} (${manager.department})` : manager.name,
    })),
  ];

  return (
    <ListSearchBox
      onSearch={onSearch}
      onClear={onClear}
      /* 필터가 많아 세로를 먹는다. 접힘 여부는 보는 사람 취향이라 브라우저가 기억한다 */
      collapsibleKey="promotion-schedule"
      activeCount={activeCount}
    >
      {/*
        🔴 한 줄에 하나씩 놓지 않는다.

        `SearchBox` 는 행마다 아래 여백 20px + 안쪽 여백 20px + 구분선을 붙인다.
        필터 8개를 8행으로 펼치면 표가 화면 밖으로 밀리는데, 그 자리에 담긴 정보는
        컨트롤 8개뿐이다. 주문/배송관리·멀티채널 주문관리(2~3년 전 화면)가
        한 행에 관련 조건을 묶어 담는 이유가 이것이다 — 여기도 성격이 같은 것끼리 묶어 4행으로 둔다.
      */}
      <BaseForm
        label="기간"
        content={
          <StyledFieldRow>
            <DatePicker.RangePicker
              value={filter.from && filter.to ? [dayjs(filter.from), dayjs(filter.to)] : null}
              /**
               * 기간 판정은 **겹침**이다 (타임라인과 같다) — 고른 구간에 조금이라도 걸치면 나온다.
               * "이 구간 안에 통째로 들어가는 것만" 이 아니라서 건수가 타임라인과 일치한다.
               */
              onChange={(dates) =>
                patch({
                  from: dates?.[0]?.format(DATE_FORMAT),
                  to: dates?.[1]?.format(DATE_FORMAT),
                })
              }
            />
            <StyledHint>고른 구간과 기간이 겹치면 조회됩니다 (타임라인과 같은 판정).</StyledHint>
          </StyledFieldRow>
        }
      />

      <BaseForm
        label="분류"
        content={
          <StyledFieldRow>
            <StyledLabeled>
              <span className="sub-label">트랙</span>
              <Select
                mode="multiple"
                allowClear={true}
                style={{ width: FILTER_WIDTH }}
                placeholder="전체"
                value={filter.tracks ?? []}
                options={TRACK_OPTIONS}
                onChange={(tracks: PromotionScheduleTrack[]) => patch({ tracks })}
              />
            </StyledLabeled>

            <StyledLabeled>
              <span className="sub-label">상태</span>
              <Select
                mode="multiple"
                allowClear={true}
                style={{ width: FILTER_WIDTH }}
                placeholder="전체"
                value={filter.statuses ?? []}
                options={STATUS_OPTIONS}
                onChange={(statuses: PromotionScheduleStatus[]) => patch({ statuses })}
              />
            </StyledLabeled>

            <StyledLabeled>
              <span className="sub-label">유형</span>
              <Select
                mode="multiple"
                allowClear={true}
                style={{ width: FILTER_WIDTH }}
                placeholder="전체"
                value={filter.types ?? []}
                options={TYPE_OPTIONS}
                onChange={(types: PromotionScheduleType[]) => patch({ types })}
              />
            </StyledLabeled>
          </StyledFieldRow>
        }
      />

      <BaseForm
        label="담당·연결"
        content={
          <StyledFieldRow>
            <StyledLabeled>
              <span className="sub-label">담당자</span>
              <Select
                mode="multiple"
                allowClear={true}
                showSearch={true}
                optionFilterProp="label"
                style={{ width: FILTER_WIDTH }}
                placeholder="전체"
                value={filter.adminIds ?? []}
                options={managerOptions}
                onChange={(adminIds: number[]) => patch({ adminIds })}
              />
            </StyledLabeled>

            <StyledLabeled>
              <span className="sub-label">기획전 연결</span>
              <Select
                allowClear={true}
                style={{ width: 140 }}
                placeholder="상관없음"
                value={filter.hasEvent}
                options={LINKED_OPTIONS}
                onChange={(hasEvent?: boolean) => patch({ hasEvent })}
              />
            </StyledLabeled>

            <StyledLabeled>
              <span className="sub-label">쿠폰 연결</span>
              <Select
                allowClear={true}
                style={{ width: 140 }}
                placeholder="상관없음"
                value={filter.hasCoupon}
                options={LINKED_OPTIONS}
                onChange={(hasCoupon?: boolean) => patch({ hasCoupon })}
              />
            </StyledLabeled>
          </StyledFieldRow>
        }
      />

      <BaseForm
        label="검색어"
        content={
          <Input
            style={{ width: 420 }}
            placeholder="항목명 · 메모"
            value={filter.keyword ?? ''}
            allowClear={true}
            onChange={(event) => patch({ keyword: event.target.value })}
            onPressEnter={onSearch}
          />
        }
      />
    </ListSearchBox>
  );
};
